import { useCallback, useEffect, useRef } from "react";

import type {
  ColumnEventType,
  ColumnResizedEvent,
  ColumnState,
  GridApi,
} from "ag-grid-community";

// Resizes the user performed on a column header: dragging its edge, or
// double-clicking the edge to autosize. Every other source (flex recalculation,
// applyColumnState, aligned-grid sync) is the grid sizing itself and must stay
// free to recompute.
const USER_RESIZE_SOURCES: ReadonlySet<ColumnEventType> = new Set([
  "uiColumnResized",
  "autosizeColumns",
]);

// AG Grid's own default width for a column whose def specifies none — what a
// freshly created column gets before minWidth clamping.
const DEFAULT_COLUMN_WIDTH = 200;

// Columns the user pinned by dragging, per grid. Read by the column-visibility
// reflow, which restores flex and must leave these widths alone.
const manualWidthColIds = new WeakMap<GridApi, Set<string>>();

/** Whether the user pinned this column's width by resizing it. */
export const hasManualWidth = (api: GridApi, colId: string): boolean =>
  manualWidthColIds.get(api)?.has(colId) ?? false;

/**
 * Marks the columns of restored state that arrive already pinned. A resize in
 * an earlier session is persisted as `flex: null` and never passes through
 * captureResizedWidths, so without this the first reflow would undo it.
 */
export const seedManualWidths = (api: GridApi): void => {
  const restored = api
    .getColumnState()
    .filter((column) => column.flex == null)
    .map((column) => column.colId);
  manualWidthColIds.set(
    api,
    new Set([...(manualWidthColIds.get(api) ?? []), ...restored]),
  );
};

/**
 * Keeps the widths a user dragged from being undone the next time AG Grid
 * applies the `columnDefs` prop.
 *
 * `defaultColDef.flex` is merged into every column, and re-applying columnDefs
 * restores each column's `flex` from that merged def — including on a column
 * whose flex the manual resize had just cleared. Flex sizing then recomputes the
 * width and the drag is lost, so the dragged widths are captured on resize and
 * re-applied (with flex cleared again) after the rebuild.
 *
 * `widthScale` identifies the active narrow/medium/wide preset and
 * `sizingResetToken` marks an explicit preset click. Either changing drops the
 * captured widths and resets every column to its def sizing: re-applying defs
 * alone restores flex but keeps a dragged column's pixel width (AG Grid only
 * resets width when the def carries an explicit `width`), so without the reset
 * a preset click would leave dragged columns where they were.
 */
export const useResizedColumnWidths = (
  gridApi: GridApi | null,
  columnDefs: readonly unknown[],
  widthScale: number,
  sizingResetToken?: number,
): ((event: ColumnResizedEvent) => void) => {
  const widthByColId = useRef(new Map<string, number>());

  // Captures every event of the drag, not just the finished one: a rebuild
  // landing mid-drag resets the width the same way, which reads as the column
  // jumping back and forth under the cursor.
  const captureResizedWidths = useCallback((event: ColumnResizedEvent) => {
    if (!USER_RESIZE_SOURCES.has(event.source)) return;
    // A resize pins the columns it sized by clearing their flex. Any other
    // column the event reports is still flexing and has to keep doing so.
    const pinnedByResize = (event.columns ?? []).filter(
      (column) => column.getFlex() == null,
    );
    widthByColId.current = new Map([
      ...widthByColId.current,
      ...pinnedByResize.map((column): [string, number] => [
        column.getColId(),
        column.getActualWidth(),
      ]),
    ]);
    // Added to, never replaced: the registry also holds columns seeded from
    // restored state, which widthByColId never sees. Only a preset reset
    // clears it.
    manualWidthColIds.set(
      event.api,
      new Set([
        ...(manualWidthColIds.get(event.api) ?? []),
        ...widthByColId.current.keys(),
      ]),
    );
  }, []);

  // Compared against the previous render so the reset runs only when the
  // preset actually changed — not on mount or when gridApi arrives, where it
  // would clobber the column state restored from session persistence.
  const prevPresetRef = useRef({ widthScale, sizingResetToken });
  useEffect(() => {
    const prev = prevPresetRef.current;
    const presetUnchanged =
      prev.widthScale === widthScale &&
      prev.sizingResetToken === sizingResetToken;
    if (presetUnchanged) return;
    prevPresetRef.current = { widthScale, sizingResetToken };

    widthByColId.current = new Map();
    if (!gridApi) return;
    manualWidthColIds.delete(gridApi);
    // Reproduces the state of a freshly created grid, in two passes because
    // applyColumnState ignores the width in any entry whose flex is non-null:
    //
    // 1. Pin every column to its creation width — def.width, or AG Grid's
    //    200 default, clamped here because applyColumnState silently skips
    //    (rather than clamps) a width below the column's minWidth.
    // 2. Restore each column's def flex flag (widths untouched). From here
    //    the grid behaves exactly as after a fresh load: environments whose
    //    layout fires flex passes (viewport/panel resizes) fill the columns
    //    out; nothing in the state API can force that pass directly.
    //
    // Deferred a macrotask so the grid has applied this render's defs first.
    // The aligned RatesGrid/SummaryGrid are not touched here: they follow via
    // the alignment metrics this reset re-emits (their defs pin the reported
    // widths), and resetting them from their own defs would restore the
    // pre-reset dragged widths those defs still embed.
    const timeout = setTimeout(() => {
      const columns = gridApi.getAllGridColumns();
      gridApi.applyColumnState({
        state: columns.map((column): ColumnState => {
          const def = column.getColDef();
          return {
            colId: column.getColId(),
            flex: null,
            width: Math.max(
              def.width ?? DEFAULT_COLUMN_WIDTH,
              column.getMinWidth(),
            ),
          };
        }),
      });
      gridApi.applyColumnState({
        state: columns.map((column): ColumnState => {
          const def = column.getColDef();
          return {
            colId: column.getColId(),
            flex: def.flex != null && def.flex > 0 ? def.flex : null,
          };
        }),
      });
    }, 0);
    return () => clearTimeout(timeout);
  }, [gridApi, widthScale, sizingResetToken]);

  useEffect(() => {
    if (!gridApi) return;
    const state: ColumnState[] = [...widthByColId.current].map(
      ([colId, width]) => ({ colId, width, flex: null }),
    );
    if (state.length === 0) return;
    gridApi.applyColumnState({ state });
  }, [gridApi, columnDefs]);

  return captureResizedWidths;
};
